<script setup>
import {backend} from "@/helpers";
import AddUsers from "../../components/AddUsers.vue";
import AppPage from "@/components/AppPage.vue";
import InputText from "primevue/inputtext";
import Badge from "primevue/badge";
import Select from "primevue/select";
import {computed, onMounted, ref} from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Chip from 'primevue/chip';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import ConfirmPopup from 'primevue/confirmpopup';
import {useConfirm} from 'primevue/useconfirm';

import hOSAvatar from "@/components/hOSAvatar.vue";
import {useRouter} from "vue-router";
import useUserRole from "@/composables/useUserRole";

const users = ref();
const selected = ref(null);
const nameFilter = ref("");
const roleFilter = ref("");
const availableRoles = ['leader', 'reviewer', 'admin', 'approver'];
const roleMenus = ref({});
const confirm = useConfirm();
const filterOptions = ref([
  {label: 'All roles', value: ''},
  {label: 'Leaders', value: 'leader'},
  {label: 'Reviewers', value: 'reviewer'},
  {label: 'Admins', value: 'admin'}
]);
const loaded = ref(false);
onMounted(async () => {
  await refreshUsers();
  loaded.value = true;
});

const filteredUsers = computed(() => {
  let filteredUsers = users.value;
  switch (roleFilter.value) {
    case 'leader':
      filteredUsers = filteredUsers.filter(u => useUserRole(u).isLeader);
      break;
    case 'admin':
      filteredUsers = filteredUsers.filter(u => useUserRole(u).isAdmin);
      break;
    case 'reviewer':
      filteredUsers = filteredUsers.filter(u => useUserRole(u).isReviewer);
      break;
  }
  let lowerFilter = nameFilter.value.toLowerCase();
  if (lowerFilter.length > 0)
    filteredUsers = filteredUsers.filter(u => u.name.toLowerCase().indexOf(lowerFilter) >= 0);
  return filteredUsers;
})

async function refreshUsers(resetSelection) {
  // loaded.value = false;
  users.value = await backend.users();
  if (resetSelection) selected.value = null;
  // loaded.value = true;
}

async function removeRole(user, roleToRemove) {
  const updatedRoles = user.roles.filter(role => role !== roleToRemove);
  await backend.editUser({
    id: user.id,
    roles: updatedRoles
  });
  await refreshUsers();
}

function getRoleMenuItems(user) {
  const userRoles = user.roles || [];
  return availableRoles
    .filter(role => !userRoles.includes(role))
    .map(role => ({
      label: role,
      command: () => addRole(user, role)
    }));
}

function toggleRoleMenu(event, user) {
  roleMenus.value[user.id]?.toggle(event);
}

async function addRole(user, roleToAdd) {
  const updatedRoles = [...(user.roles || []), roleToAdd];
  await backend.editUser({
    id: user.id,
    roles: updatedRoles
  });
  await refreshUsers();
}

async function deactivateUser(event, user) {
  confirm.require({
    target: event.currentTarget,
    message: `Are you sure you want to deactivate ${user.name}?`,
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined p-button-sm',
    acceptClass: 'p-button-sm p-button-danger',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    accept: async () => {
      await backend.deleteUser({id: user.id});
      await refreshUsers();
    }
  });
}

const router = useRouter();
function openUser(user) {
  router.push('/profile/' + user.username)
}
</script>
<template>
  <AppPage title="Users" :loaded="loaded">
    <ConfirmPopup />
    <div class="flex gap-2">
      <Select v-model="roleFilter" :options="filterOptions" optionLabel="label" optionValue="value"
              placeholder="by role"/>
      <InputText v-model="nameFilter" placeholder="by name" class="xl:w-auto"/>
    </div>
    <DataTable
        :value="filteredUsers"
        scrollable
        scroll-height="65vh"
        style="min-height: 65vh"
    >
      <Column style="width: 1rem">
        <template #body="{data}">
          <hOSAvatar :user="data" size="small"/>
        </template>
      </Column>
      <Column field="name" header="Name" :sortable="true">
        <template #body="{data}">
          <span class="cursor-pointer hover:text-primary" @click="openUser(data)">{{ data.name }}</span>
        </template>
      </Column>
      <Column field="email" header="Email" :sortable="true">
        <template #body="{data}">
          <span class="cursor-pointer hover:text-primary" @click="openUser(data)">{{ data.email }}</span>
        </template>
      </Column>
      <Column field="pulse_enabled" header="Pulse" :sortable="true" style="width: 1rem">
        <template #body="{data}">
          <i class="pi pi-check" v-if="data.pulse_enabled"/>
        </template>
      </Column>
      <Column header="Roles">
        <template #body="{data}">
          <div class="flex flex-row gap-2 items-center">
            <Chip :label="r" v-for="r in data.roles" severity="contrast" class="text-sm" :key="r" removable @remove="removeRole(data, r)"/>
            <Button icon="pi pi-plus" size="small" text rounded @click="toggleRoleMenu($event, data)" :disabled="getRoleMenuItems(data).length === 0"/>
            <Menu :ref="el => roleMenus[data.id] = el" :model="getRoleMenuItems(data)" popup/>
          </div>
        </template>
      </Column>
      <Column>
        <template #body="{data}">
          <Badge :value="data.people?.length" severity="secondary" v-if="useUserRole(data).isLeader"/>
        </template>
      </Column>
      <Column header="Actions" style="width: 1rem">
        <template #body="{data}">
          <Button icon="pi pi-ban" size="small" severity="danger" text rounded @click="deactivateUser($event, data)" title="Deactivate user"/>
        </template>
      </Column>
    </DataTable>
    <div class="grid grid-cols-12 gap-4 justify-start">
      <div class="col-span-4 flex flex-col">

      </div>
      <div class="col-span-8">
      </div>
    </div>

    <AddUsers @refresh-users="refreshUsers"/>

  </AppPage>
</template>
